import * as React from 'react';
import {useState, useEffect} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";

import PaymentForm from '../components/PaymentForm';


import AppTheme from '../shared-theme/AppTheme';
import ColorModeIconDropdown from '../shared-theme/ColorModeIconDropdown';

import {getUserCredit, createMontonioPayment, handleBuyPlan} from "../api/planApi";
import { acceptContract } from "../api/contractApi";
import { validateDiscountCode } from "../api/discountCodeApi";

export default function Checkout(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Kasuta olekus vaikimisi tühja objekti
    const [affiliateInfo, setAffiliateInfo] = useState({});
    const [planData, setPlanData] = useState({});
    const [contract, setContract] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paymentError, setPaymentError] = useState(null);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [invoiceNumber, setInvoiceNumber] = useState(null);
    // Krediidi olekud
    const [appliedCredit, setAppliedCredit] = useState(0);
    const [affiliateCredit, setAffiliateCredit] = useState(0);
    const [userData, setUserData] = useState(null);
    const [merchantReference, setMerchantReference] = useState(null);
    const [isContractPayment, setIsContractPayment] = useState(false);
    const [isFamilyMember, setIsFamilyMember] = useState(false);
    const [familyMemberId, setFamilyMemberId] = useState(null);

    const [isPlanStartDate, setIsPlanStartDate] = useState(false);
    const [planStartDate, setPlanStartDate] = useState(0);
    const [discountCode, setDiscountCode] = useState('');
    const [appliedDiscount, setAppliedDiscount] = useState(null); // { valid, discountAmount, message } or null
    const [affiliatePricingPlan, setAffiliatePricingPlan] = useState(null);
    const [selectedMontonioPaymentMethod, setSelectedMontonioPaymentMethod] = useState('paymentInitiation');

    // Clear discount on every checkout page visit so stale codes are not reused
    useEffect(() => {
        setDiscountCode('');
        setAppliedDiscount(null);
        localStorage.removeItem('checkout_discountCode');
        localStorage.removeItem('checkout_appliedDiscount');
    }, []);

    // Lae andmed localStorage'ist esimesel renderil
    useEffect(() => {
        // Proovi kõigepealt saada andmed location.state'ist
        const plan = location.state?.plan;
        const affiliate = location.state?.affiliate;
        const contractData = location.state?.contract;
        const userData = location.state?.userData;
        const isContractPmt = location.state?.isContractPayment;
        const familyMember = location.state?.familyMember;
        const familyMemberId = location.state?.familyMemberId;
        const isPlanStartDateValue = location.state?.isPlanStartDate;
        const planStartDateValue = location.state?.planStartDate;



        if (plan) {
            setPlanData(plan);
            // Kontrollime, kas tegemist on lepingumaksega
            if (plan.id === 'contract-payment') {
                setIsContractPayment(true);
            }

        }

        if (affiliate) {
            setAffiliateInfo(affiliate);

        }

        if (contractData) {
            setContract(contractData);

        }

        if (userData) {
            setUserData(userData);

        }
        if (familyMember) {
            setIsFamilyMember(true);

        }

        if (familyMemberId) {
            setFamilyMemberId(familyMemberId);

        }

        if (isContractPmt) {
            setIsContractPayment(true);

        }

        if (isPlanStartDateValue) {
            setIsPlanStartDate(isPlanStartDateValue);
        }

        if (planStartDateValue) {
            setPlanStartDate(planStartDateValue);
        }

        // Kui location.state pole saadaval, proovi localStorage'ist
        if (!plan) {
            const savedPlanData = localStorage.getItem('checkout_planData');
            if (savedPlanData) {
                try {
                    const parsedPlanData = JSON.parse(savedPlanData);
                    setPlanData(parsedPlanData);
                    // Kontrollime, kas tegemist on lepingumaksega
                    if (parsedPlanData.id === 'contract-payment') {
                        setIsContractPayment(true);
                    }
                } catch (error) {
                    console.error('Error parsing saved plan data', error);
                }
            }
        }

        // Laadi ülejäänud andmed localStorage'ist
        if (!affiliate) {
            const savedAffiliateInfo = localStorage.getItem('checkout_affiliateInfo');
            if (savedAffiliateInfo) {
                try {
                    const parsedAffiliateInfo = JSON.parse(savedAffiliateInfo);
                    setAffiliateInfo(parsedAffiliateInfo);
                } catch (error) {
                    console.error('Error parsing saved affiliate info', error);
                }
            }
        }

        if (!contractData) {
            const savedContract = localStorage.getItem('checkout_contract');
            if (savedContract) {
                try {
                    const parsedContract = JSON.parse(savedContract);
                    setContract(parsedContract);
                } catch (error) {
                    console.error('Error parsing saved contract', error);
                }
            }
        }

        if (!userData) {
                const savedUserData = localStorage.getItem('checkout_userData');
            if (savedUserData) {
                try {
                    const parsedUserData = JSON.parse(savedUserData);
                    setUserData(parsedUserData);
                } catch (error) {
                    console.error('Error parsing saved user data', error);
                }
            }
        }

        if (!familyMember) {
            const savedFamilyMember = localStorage.getItem('checkout_familyMember');
            if (savedFamilyMember) {
                try {
                    const parsedFamilyMember = JSON.parse(savedFamilyMember);
                    setIsFamilyMember(parsedFamilyMember);
                } catch (error) {
                    console.error('Error parsing saved family member', error);
                }
            }
        }

        if (!familyMemberId) {
            const savedFamilyMemberId = localStorage.getItem('checkout_familyMemberId');
            if (savedFamilyMemberId) {
                try {
                    const parsedFamilyMemberId = JSON.parse(savedFamilyMemberId);
                    setFamilyMemberId(parsedFamilyMemberId);
                } catch (error) {
                    console.error('Error parsing saved family member ID', error);
                }
            }
        }

        if (!isContractPmt) {
            const savedIsContractPayment = localStorage.getItem('checkout_isContractPayment');
            if (savedIsContractPayment === 'true') {
                setIsContractPayment(true);
            }
        }

        // Lae salvestatud rakendatud krediit
        const savedAppliedCredit = localStorage.getItem('checkout_appliedCredit');
        if (savedAppliedCredit) {
            try {
                setAppliedCredit(parseFloat(savedAppliedCredit));
            } catch (error) {
                console.error('Error parsing saved applied credit', error);
            }
        }

    }, [location.state]);



    // Funktsioon checkout andmete kustutamiseks
    const clearCheckoutData = () => {
        localStorage.removeItem('checkout_planData');
        localStorage.removeItem('checkout_affiliateInfo');
        localStorage.removeItem('checkout_contract');
        localStorage.removeItem('checkout_appliedCredit');
        localStorage.removeItem('checkout_userData');
        localStorage.removeItem('checkout_isContractPayment');
        localStorage.removeItem('checkout_familyMember');
        localStorage.removeItem('checkout_familyMemberId');
        localStorage.removeItem('checkout_discountCode');
        localStorage.removeItem('checkout_appliedDiscount');
        localStorage.removeItem('checkout_token');
        localStorage.removeItem('checkout_role');
        localStorage.removeItem('checkout_pricingPlan');
        localStorage.removeItem('checkout_payment_in_progress');
    };

    // Persist discount code to localStorage for checkout restore
    useEffect(() => {
        if (discountCode) localStorage.setItem('checkout_discountCode', discountCode);
        else localStorage.removeItem('checkout_discountCode');
        if (appliedDiscount) localStorage.setItem('checkout_appliedDiscount', JSON.stringify(appliedDiscount));
        else localStorage.removeItem('checkout_appliedDiscount');
    }, [discountCode, appliedDiscount]);

    // Lae affiliaadi krediidi info
    useEffect(() => {

        if (affiliateInfo?.id) {
            getUserCredit(affiliateInfo.id)
                .then((data) => {
                    setAffiliateCredit((data && typeof data.credit === "number") ? data.credit : 0);
                    setAffiliatePricingPlan(data?.pricingPlan || null);
                })
                .catch((error) => {
                    console.error("Error fetching user credit:", error);
                });
        }
    }, [affiliateInfo?.id]);

    const isMontonioBankPaymentEnabled = affiliateInfo?.isMontonioBankPaymentEnabled !== false;
    const isMontonioCardPaymentEnabled = affiliateInfo?.isMontonioCardPaymentEnabled === true;
    const isBlikPaymentAvailable = affiliateInfo?.paymentCurrency === 'PLN'
        && affiliateInfo?.isMontonioBlikPaymentEnabled === true;
    const isMobilePayPaymentAvailable = affiliateInfo?.paymentCountry === 'FI'
        && affiliateInfo?.paymentCurrency === 'EUR'
        && isMontonioCardPaymentEnabled;
    const availableMontonioPaymentMethods = [
        ...(isMontonioBankPaymentEnabled ? [{ value: 'paymentInitiation', label: 'Bank payment' }] : []),
        ...(isMontonioCardPaymentEnabled ? [{ value: 'cardPayments', label: 'Card payment' }] : []),
        ...(isBlikPaymentAvailable ? [{ value: 'blik', label: 'BLIK' }] : []),
        ...(isMobilePayPaymentAvailable ? [{ value: 'mobilePay', label: 'MobilePay' }] : []),
    ];

    useEffect(() => {
        const availableValues = [
            ...(isMontonioBankPaymentEnabled ? ['paymentInitiation'] : []),
            ...(isMontonioCardPaymentEnabled ? ['cardPayments'] : []),
            ...(isBlikPaymentAvailable ? ['blik'] : []),
            ...(isMobilePayPaymentAvailable ? ['mobilePay'] : []),
        ];
        if (availableValues.length > 0 && !availableValues.includes(selectedMontonioPaymentMethod)) {
            setSelectedMontonioPaymentMethod(availableValues[0]);
        }
    }, [isMontonioBankPaymentEnabled, isMontonioCardPaymentEnabled, isBlikPaymentAvailable, isMobilePayPaymentAvailable, selectedMontonioPaymentMethod]);

    // Uuendatud handlePlaceOrder ilma buyPlan ja acceptContract käskudeta
    // Inside the Checkout.jsx file, the handlePlaceOrder function needs to be modified



    const handlePlaceOrder = async () => {
        try {
            setLoading(true);

            // Validate affiliate and plan consistency
            if (planData.affiliateId !== affiliateInfo.id) {

                setPaymentError('Data inconsistency error. Please go back and select the plan again.');
                setLoading(false);
                return;
            }

            const isPremiumAffiliate = affiliatePricingPlan === 'premium';
            const isContractPmt = isContractPayment || planData?.id === 'contract-payment';
            const basePrice = isContractPmt && contract?.isFirstPayment && contract?.firstPaymentAmount
                ? contract.firstPaymentAmount
                : (planData?.price || 0);
            const parsedPlanId = parseInt(planData?.id, 10);
            const planId = Number.isNaN(parsedPlanId) ? null : parsedPlanId;
            let currentAppliedDiscount = appliedDiscount;

            if (appliedDiscount?.valid && discountCode?.trim()) {
                const revalidated = await validateDiscountCode(
                    discountCode.trim(),
                    affiliateInfo.id,
                    basePrice,
                    planId
                );

                if (!revalidated?.valid) {
                    setAppliedDiscount(revalidated);
                    setPaymentError(revalidated?.message || 'Discount code is not valid for this plan.');
                    setLoading(false);
                    return;
                }

                setAppliedDiscount(revalidated);
                currentAppliedDiscount = revalidated;
            }

            // Arvuta lõplik summa (algsummast lahutatakse krediit ja sooduskood)
            const discountAmount = currentAppliedDiscount?.valid ? (currentAppliedDiscount?.discountAmount || 0) : 0;
            let finalAmount;

            // Round to 2 decimals to avoid floating point issues (e.g. 3 - 2.7 - 0.3 = 4.44e-17)
            finalAmount = parseFloat(Math.max(basePrice - appliedCredit - discountAmount, 0).toFixed(2));

            if (!isPremiumAffiliate && finalAmount > 0) {
                setPaymentError('This affiliate plan supports checkout by credit only. Bank payment/Montonio is not available.');
                setLoading(false);
                return;
            }

            // Kui kogu summa on kaetud krediidiga, saada info backendile
            if (finalAmount === 0) {
                try {
                    // Genereerime unikaalse merchantReference
                    const creditMerchantReference = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14);

                    if (isContractPmt && contract) {
                        // Kui on tegemist lepingumaksega ja summa on 0, siis kutsume otse acceptContract
                        try {
                            const contractId = contract.id;
                            const acceptType = "checkbox";
                            const userId = userData?.id;
                            const affiliateId = affiliateInfo?.id;
                            // Leiame contract terms ID - tavaliselt 1, kuid võibolla vaja dünaamiliselt laadida
                            const contractTermsId = 1;

                            // Kutsu acceptContract API
                            const result = await acceptContract(contractId, {
                                userId,
                                affiliateId,
                                acceptType,
                                contractTermsId,
                                contractId,
                                appliedCredit,
                                invoiceNumber: creditMerchantReference,
                            });





                            // Kustutame checkout andmed
                            clearCheckoutData();
                            localStorage.setItem("plan_last_order_ref", creditMerchantReference);

                            // Suuname staatuse lehele
                            navigate(`/after-checkout?source=plan&ref=${encodeURIComponent(creditMerchantReference)}`);
                        } catch (contractError) {
                            console.error("Error accepting contract:", contractError);
                            setPaymentError('Error accepting contract');
                            setLoading(false);
                        }
                    } else {
                        // Kui pole lepingumakse, siis tavaline planData ost krediidiga
                        const data = {
                            planData: planData,
                            currentAppliedCredit: appliedCredit,
                            contract: contract,
                            currentMerchantReference: creditMerchantReference,
                            currentIsContractPayment: isContractPmt || false,
                            isFamilyMember: isFamilyMember || false,
                            familyMemberId: familyMemberId || null
                        };


                        const discountAmountForCredit = currentAppliedDiscount?.valid ? (currentAppliedDiscount?.discountAmount || 0) : 0;
                        const discountCodeIdForCredit = currentAppliedDiscount?.valid ? (currentAppliedDiscount?.discountCodeId || null) : null;
                        const result = await handleBuyPlan(
                            planData,
                            affiliateInfo.id,
                            appliedCredit,
                            contract,
                            creditMerchantReference,
                            isContractPmt || false,
                            isFamilyMember || false,
                            familyMemberId || null,
                            isPlanStartDate || false,
                            planStartDate || 0,
                            discountAmountForCredit,
                            discountCodeIdForCredit
                        );



                        // Kustutame checkout andmed
                        clearCheckoutData();
                        localStorage.setItem("plan_last_order_ref", creditMerchantReference);

                        // Suuname staatuse lehele
                        navigate(`/after-checkout?source=plan&ref=${encodeURIComponent(creditMerchantReference)}`);
                    }
                } catch (error) {
                    console.error("Error with credit payment:", error);
                    setPaymentError('Payment with credit failed');
                    setLoading(false);
                }
            } else {
                // Kui on vaja kasutada Montoniot (jätame olemasoleva koodi siia)
                const returnUrl = window.location.href.split('?')[0]; // kept for API compatibility

                // Loo vajalikud andmed vastavalt maksele
                const paymentPrice = isContractPmt && contract?.isFirstPayment && contract?.firstPaymentAmount
                    ? contract.firstPaymentAmount
                    : planData?.price;

                // Uuenda planData hinda kui tegemist on esimese lepingumaksega
                const adjustedPlanData = {...planData};
                if (isContractPmt && contract?.isFirstPayment && contract?.firstPaymentAmount) {
                    adjustedPlanData.price = contract.firstPaymentAmount;
                }

                const { id, fullName, email } = userData;
                const userDataForApi = { id, fullName, email };
                const discountAmountForPayment = currentAppliedDiscount?.valid ? (currentAppliedDiscount?.discountAmount || 0) : 0;
                const discountCodeIdForPayment = currentAppliedDiscount?.valid ? (currentAppliedDiscount?.discountCodeId || null) : null;
                const paymentResponse = await createMontonioPayment(
                    adjustedPlanData,
                    affiliateInfo?.id,
                    appliedCredit,
                    contract,
                    returnUrl,
                    userDataForApi,
                    isFamilyMember,
                    familyMemberId,
                    isPlanStartDate,
                    planStartDate,
                    discountAmountForPayment,
                    discountCodeIdForPayment,
                    selectedMontonioPaymentMethod
                );




                if (paymentResponse.payment_url) {
                    const paymentMerchantReference = paymentResponse.merchantReference || null;

                    // Kustuta checkout andmed
                    clearCheckoutData();
                    if (paymentMerchantReference) {
                        localStorage.setItem("plan_pending_order_ref", paymentMerchantReference);
                        localStorage.setItem("plan_last_order_ref", paymentMerchantReference);
                    }

                    // Redirect samas aknas (PWA-safe)
                    window.location.assign(paymentResponse.payment_url);
                } else {
                    throw new Error('Error payment response');
                }
            }
        } catch (error) {
            console.error(error);
            setPaymentError('Payment failed');
            setLoading(false);
        }
    };

    const basePriceForTotal = (isContractPayment || planData?.id === 'contract-payment') && contract?.isFirstPayment && contract?.firstPaymentAmount
        ? contract.firstPaymentAmount
        : (planData?.price || 0);
    const discountAmount = appliedDiscount?.valid ? (appliedDiscount?.discountAmount || 0) : 0;
    const totalPrice = parseFloat(Math.max(basePriceForTotal - appliedCredit - discountAmount, 0).toFixed(2));
    const isPremiumAffiliate = affiliatePricingPlan === 'premium';

    // Lisa laadimise ja vigade käsitlemise renderdus
    if (loading) {
        return (
            <AppTheme {...props}>
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
                    <CircularProgress/>
                    <Typography variant="h6" sx={{ml: 2}}>Processing payment...</Typography>
                </Box>
            </AppTheme>
        );
    }

    if (paymentError) {
        return (
            <AppTheme {...props}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    p: 3
                }}>
                    <Typography variant="h5" color="error" gutterBottom>Payment error</Typography>
                    <Typography variant="body1">{paymentError}</Typography>
                    <Button
                        variant="contained"
                        sx={{mt: 3}}
                        onClick={() => {
                            setPaymentError(null);
                        }}
                    >
                        Try again
                    </Button>
                </Box>
            </AppTheme>
        );
    }

    return (
        <AppTheme {...props}>
            <CssBaseline enableColorScheme/>
            <Box sx={{position: 'fixed', top: '0rem', right: '1rem', zIndex: 10}}>
                <ColorModeIconDropdown/>
            </Box>
            <Container maxWidth="lg" sx={{ py: { xs: 1.5, md: 3 } }}>
                <Card
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 3 },
                        mb: 2,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        background: 'linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,1) 45%)'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ShoppingBagIcon color="primary" />
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    Checkout
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Review your plan and complete payment securely.
                                </Typography>
                            </Box>
                        </Box>
                        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate("/register-training")}>
                            Back
                        </Button>
                    </Box>
                </Card>

                <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3, border: 'none', boxShadow: 'none' }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                                    Payment details
                                </Typography>

                                <PaymentForm
                                    affiliateCredit={affiliateCredit}
                                    appliedCredit={appliedCredit}
                                    setAppliedCredit={setAppliedCredit}
                                    planPrice={(contract?.isFirstPayment && contract?.firstPaymentAmount) ? contract.firstPaymentAmount : (planData?.price || 0)}
                                    affiliateInfo={affiliateInfo}
                                    planName={planData?.name || ''}
                                    discountCode={discountCode}
                                    setDiscountCode={setDiscountCode}
                                    appliedDiscount={appliedDiscount}
                                    setAppliedDiscount={setAppliedDiscount}
                                    affiliateId={affiliateInfo?.id}
                                    planId={Number.isNaN(parseInt(planData?.id, 10)) ? null : parseInt(planData?.id, 10)}
                                    isPremiumAffiliate={affiliatePricingPlan === 'premium'}
                                />

                                {totalPrice > 0 && isPremiumAffiliate && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            Payment method
                                        </Typography>
                                        {availableMontonioPaymentMethods.length > 1 ? (
                                            <FormControl component="fieldset">
                                                <RadioGroup
                                                    value={selectedMontonioPaymentMethod}
                                                    onChange={(event) => setSelectedMontonioPaymentMethod(event.target.value)}
                                                >
                                                    {availableMontonioPaymentMethods.map((method) => (
                                                        <FormControlLabel
                                                            key={method.value}
                                                            value={method.value}
                                                            control={<Radio />}
                                                            label={method.label}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                                <FormHelperText>
                                                    Choose how you want to pay with Montonio.
                                                </FormHelperText>
                                            </FormControl>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {availableMontonioPaymentMethods[0]?.label || 'Bank payment'}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        pt: 2,
                                        pb: { xs: 1, sm: 0 },
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        endIcon={<ChevronRightRoundedIcon />}
                                        onClick={handlePlaceOrder}
                                        sx={{ width: { xs: '100%', sm: 'fit-content' } }}
                                        disabled={!isPremiumAffiliate && totalPrice > 0}
                                    >
                                        {totalPrice === 0
                                            ? 'Pay with credit'
                                            : (isPremiumAffiliate ? 'Pay with Montonio' : 'Insufficient credit')}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Box sx={{ height: { xs: 'calc(90px + env(safe-area-inset-bottom, 0px))', md: 88 } }} />
            </Container>
        </AppTheme>
    );
}